import { IModel } from "makerjs";
import {
	ShapeModelDecorator,
	DimensionLineDecorator,
	InterfaceModelDecorator,
	CutoutMoveDecorator,
} from "./PreviewModelDecorator";
import Cutout from "@/Models/Cutout";
import { makerjs } from "../../../../source/js/shared/rendering/makerjs";
import { chainModel } from "@/functions/makerjs";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import { GuideLineDecorator, OffsetLineDecorator } from "./DimensionLineDecorator";
import { CutoutRotationDecorator, CutoutRotationIndicatorDecorator } from "./CutoutRotationDecorator";
import { CurrentCutoutFilletModelDecorator } from "./ShapeModelDecorator";

export class CutoutModelDecorator implements InterfaceModelDecorator {
	constructor(
		private parentDecorator: InterfaceModelDecorator | null,
		private cutouts: Cutout[]
	) {}

	getModel(): IModel {
		let model = { models: {} } as IModel;

		if (this.parentDecorator) {
			model = this.parentDecorator.getModel();
		}

		if (this.cutouts.length === 0) {
			return model;
		}

		const cutoutsModel = { models: {} } as IModel;

		this.cutouts.forEach((cutout: Cutout, index: number) => {
			let modelDecorator: InterfaceModelDecorator;
			modelDecorator = new ShapeModelDecorator(null, cutout.instance);
			modelDecorator = new CutoutMoveDecorator(modelDecorator, cutout, model);
			modelDecorator = new CutoutRotationDecorator(modelDecorator, cutout);

			const cutoutModel = modelDecorator.getModel();

			makerjs.model.addModel(cutoutsModel, cutoutModel, `cutoutModel_${index}`);
		});

		let combinedModel = { models: {} } as IModel;

		Object.values(cutoutsModel.models).forEach((model: IModel) => {
			combinedModel = makerjs.model.combineUnion(combinedModel, model);
		});

		combinedModel = chainModel(combinedModel);
		makerjs.model.addModel(model, combinedModel, "cutoutModel");

		return model;
	}
}

/**
 * This decorator is used to show and add the current cutout in the preview.
 * So we can highlight this cutout seperated from the combined cutouts model.
 */
export class currentCutoutModelDecorator implements InterfaceModelDecorator {
	constructor(
		private parentDecorator: InterfaceModelDecorator | null,
		private cutout: Cutout | null,
		private focusedElement: string,
		private ratio: number,
		private shouldApplyFillet: boolean,
		private minimumDrillHoleDiameter: Length,
		private baseShapeModel: IModel
	) {}

	getModel(): IModel {
		let model = { models: {} } as IModel;

		if (this.parentDecorator) {
			model = this.parentDecorator.getModel();
		}

		if (!this.cutout) {
			return model;
		}

		let modelDecorator: InterfaceModelDecorator;
		const baseShapeInstance = this.cutout.instance;
		modelDecorator = new ShapeModelDecorator(null, baseShapeInstance);
		modelDecorator = new CutoutMoveDecorator(modelDecorator, this.cutout, this.baseShapeModel);

		modelDecorator = new DimensionLineDecorator(modelDecorator, baseShapeInstance, this.ratio, this.focusedElement);
		modelDecorator = new GuideLineDecorator(modelDecorator, baseShapeInstance, this.focusedElement);

		if(this.focusedElement?.includes("cutoutRotation")) {
			modelDecorator = new CutoutRotationIndicatorDecorator(modelDecorator, this.cutout);
		}

		// Rotate the model included the dimension lines
		modelDecorator = new CutoutRotationDecorator(modelDecorator, this.cutout);

		if(this.focusedElement?.includes("cutoutOffset")) {
			modelDecorator = new OffsetLineDecorator(modelDecorator, baseShapeInstance, this.cutout, this.focusedElement);
		}

		if (this.shouldApplyFillet) {
			modelDecorator = new CurrentCutoutFilletModelDecorator(modelDecorator, this.minimumDrillHoleDiameter, true);
		}

		let cutoutModel = modelDecorator.getModel();

		if ( typeof cutoutModel.models.shapeModel === 'undefined' ) {
			return model;
		}

		cutoutModel.models.shapeModel.layer = "active-cutout";
		cutoutModel = makerjs.cloneObject(cutoutModel);

		makerjs.model.addModel(model, cutoutModel, "currentCutout");

		makerjs.model.zero(model);
		makerjs.model.originate(model);

		return model;
	}
}
